// mp3Duration.js — compute exact MP3 duration by walking every frame.
//
// Why this exists
// ---------------
// Our TTS endpoint returns VBR MP3 without a Xing/VBRI header. WebKit
// (and, via WKWebView, the iOS app) estimates duration by extrapolating the
// first frame's bitrate across the whole file as if it were CBR — which
// for a 6-minute VBR clip can easily come out as "3:49". Worse, once the
// browser has committed to that wrong duration, it *also* clamps
// `audioEl.currentTime` to that number, so the scrubber freezes at 3:49
// while audio keeps playing.
//
// The robust fix is to just count frames ourselves. Every MPEG audio frame
// carries enough header info (version, layer, bitrate, sample rate) to
// compute its exact duration, and the sum of those per-frame durations is
// the true length of the stream — independent of whether the file is CBR,
// VBR, or even VBR-with-a-lying-header.
//
// This is a pure function: give it an ArrayBuffer, get back seconds. It
// returns 0 if the data doesn't look like a valid MPEG audio stream, so
// callers can safely fall back to the element's reported duration.

// Bitrate table (kbps). Indexed first by version group (1 = MPEG1,
// 2 = MPEG2/2.5), then by layer (1/2/3), then by bitrate index 0–15. A
// value of 0 means "free format" (not a real rate we can use here) and -1
// means "reserved / invalid".
const BITRATE_TABLE = {
  1: {
    1: [0, 32, 64, 96, 128, 160, 192, 224, 256, 288, 320, 352, 384, 416, 448, -1],
    2: [0, 32, 48, 56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, 384, -1],
    3: [0, 32, 40, 48,  56,  64,  80,  96, 112, 128, 160, 192, 224, 256, 320, -1],
  },
  2: {
    1: [0, 32, 48, 56,  64,  80,  96, 112, 128, 144, 160, 176, 192, 224, 256, -1],
    2: [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
    3: [0,  8, 16, 24,  32,  40,  48,  56,  64,  80,  96, 112, 128, 144, 160, -1],
  },
};

// Sample rate table (Hz). Keyed by MPEG version (1, 2, or 2.5 as string
// for object-key stability), then by the 2-bit samplerate index in the
// header.
const SAMPLERATE_TABLE = {
  '1':   [44100, 48000, 32000, -1],
  '2':   [22050, 24000, 16000, -1],
  '2.5': [11025, 12000,  8000, -1],
};

// Samples per frame depends on version group + layer. Layer I is always
// 384; Layer II is always 1152; Layer III is 1152 for MPEG1 and 576 for
// MPEG2/2.5. These control how much real time each frame represents.
const SAMPLES_PER_FRAME = {
  1: { 1: 384, 2: 1152, 3: 1152 },
  2: { 1: 384, 2: 1152, 3:  576 },
};

// Compute the byte size of a single MPEG audio frame given the header
// fields. Formula per layer per the MPEG-1/2 spec.
function frameByteSize(versionGroup, layer, bitrateKbps, sampleRate, padding) {
  if (layer === 1) {
    return Math.floor((12 * bitrateKbps * 1000 / sampleRate + padding) * 4);
  }
  const factor = versionGroup === 1 ? 144 : 72;
  return Math.floor(factor * bitrateKbps * 1000 / sampleRate) + padding;
}

// Skip past any ID3v2 tag at the start of the stream. Returns the offset
// of the first byte after the tag, or 0 if there's no tag.
function skipId3v2(view) {
  const len = view.byteLength;
  if (len < 10) return 0;
  if (view.getUint8(0) !== 0x49 || view.getUint8(1) !== 0x44 || view.getUint8(2) !== 0x33) return 0; // "ID3"
  // Size is four sync-safe bytes at offset 6.
  const size = ((view.getUint8(6) & 0x7F) << 21)
             | ((view.getUint8(7) & 0x7F) << 14)
             | ((view.getUint8(8) & 0x7F) << 7)
             |  (view.getUint8(9) & 0x7F);
  return Math.min(len, 10 + size);
}

// Main entry point. Returns duration in seconds, or 0 on parse failure.
export function probeMp3Duration(arrayBuffer) {
  if (!arrayBuffer || !arrayBuffer.byteLength) return 0;
  const view = new DataView(arrayBuffer);
  const length = view.byteLength;

  let pos = skipId3v2(view);

  let totalSeconds = 0;
  let frames = 0;

  while (pos + 4 <= length) {
    const b0 = view.getUint8(pos);
    const b1 = view.getUint8(pos + 1);
    // Frame sync is 11 bits of 1 — top byte 0xFF plus the three high bits
    // of the next byte.
    if (b0 !== 0xFF || (b1 & 0xE0) !== 0xE0) {
      pos++;
      continue;
    }
    const b2 = view.getUint8(pos + 2);

    // Version: 00 = 2.5, 01 = reserved, 10 = 2, 11 = 1.
    const versionBits = (b1 >> 3) & 0x03;
    // Layer: 00 = reserved, 01 = III, 10 = II, 11 = I.
    const layerBits   = (b1 >> 1) & 0x03;
    if (versionBits === 1 || layerBits === 0) { pos++; continue; }

    const versionKey   = versionBits === 3 ? '1' : versionBits === 2 ? '2' : '2.5';
    const versionGroup = versionKey === '1' ? 1 : 2;
    const layer = 4 - layerBits; // 01->3, 10->2, 11->1

    const bitrateIndex    = (b2 >> 4) & 0x0F;
    const sampleRateIndex = (b2 >> 2) & 0x03;
    const padding         = (b2 >> 1) & 0x01;

    const bitrate    = BITRATE_TABLE[versionGroup][layer][bitrateIndex];
    const sampleRate = SAMPLERATE_TABLE[versionKey][sampleRateIndex];
    if (!bitrate || bitrate <= 0 || !sampleRate || sampleRate <= 0) { pos++; continue; }

    const samplesPerFrame = SAMPLES_PER_FRAME[versionGroup][layer];
    const frameSize = frameByteSize(versionGroup, layer, bitrate, sampleRate, padding);
    if (frameSize < 4 || pos + frameSize > length) {
      // Incomplete final frame — stop here rather than over-counting.
      break;
    }

    totalSeconds += samplesPerFrame / sampleRate;
    frames++;
    pos += frameSize;
  }

  // Require a sensible minimum to avoid returning a nonsense tiny value
  // when we only stumbled across a single plausible-looking byte sequence
  // in non-MP3 data.
  return frames >= 4 ? totalSeconds : 0;
}
